import { Plus, X } from '@phosphor-icons/react'
import { Input, Switch } from 'antd'
import { infoMessage } from 'api/global'
import CustomModal from 'components/global/CustomModal'
import CustomModalFooter from 'components/global/CustomModalFooter'
import React, { useState } from 'react'
import { leagueEventStore } from 'state/league/leagueEvent'
import { classesEventStore } from 'state/classes/classesStore'

interface DonationModalProps {
  openModal: boolean
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>
  reference: 'event' | 'league' | 'class'
}

const DonationModal: React.FC<DonationModalProps> = ({
  openModal,
  setOpenModal,
  reference,
}) => {
  const { donationDataLeague, setDonationDataLeague } = leagueEventStore(
    store => ({
      donationDataLeague: store.donationData,
      setDonationDataLeague: store.setDonationData,
    }),
  )
  
  const { donationDataClass, setDonationDataClass } = classesEventStore(
    store => ({
      donationDataClass: store.donationData,
      setDonationDataClass: store.setDonationData,
    }),
  )
  
  const donationData = reference === 'league' ? donationDataLeague : donationDataClass
  const [enabled, setEnabled] = useState<boolean>(
    donationData.allowDonation,
  )
  const [preselected, setPreselected] = useState<number[]>(
    donationData.preSelectedDonationAmounts,
  )
  const [newValue, setNewValue] = useState<string>('')

  const handleClose = () => {
    setOpenModal(false)
  }

  const onToggle = (checked: boolean) => {
    setEnabled(checked)
  }

  const validateAmount = (val: string): number | null => {
    if (!val) return null
    const normalized = val.replace(/[^0-9.]/g, '')
    const num = parseFloat(normalized)
    if (Number.isNaN(num) || num <= 0) return null
    return Math.round(num * 100) / 100
  }

  const handleAddValue = () => {
    const num = validateAmount(newValue)
    if (!num && preselected.length === 0) {
      infoMessage('Enter a valid amount greater than 0')
      return
    }
    if (preselected.includes(num)) {
      infoMessage('This amount is already added')
      return
    }
    const next = [...preselected, num].sort((a, b) => a - b)
    setPreselected(next)
    setNewValue('')
  }

  const handleSubmit = () => {
    const num = validateAmount(newValue)
    if (!num && preselected.length === 0) {
      infoMessage('Enter a valid amount greater than 0')
      return
    }
    if (num && preselected.includes(num)) {
      infoMessage('This amount is already added')
      return
    }
    const state = {
      allowDonation: enabled,
      preSelectedDonationAmounts: num ? [...preselected, num].sort((a, b) => a - b) : preselected,
    }
    if (reference === 'league') {
      setDonationDataLeague(state)
      handleClose()
    } else if (reference === 'event') {
      infoMessage('Setup states for event')
    } else if (reference === 'class') {
      setDonationDataClass(state)
      handleClose()
    } else {
      infoMessage('Nothing with this reference exist')
    }
  }

  return (
    <>
      <CustomModal
        openModal={openModal}
        isCloseModalCustom={true}
        closeModal={handleClose}
        title="Donation"
        footer={
          <CustomModalFooter
            multibutton={false}
            btnAlignment="justify-end"
            btnText="Save"
            btnType="primary"
            closeModal={handleClose}
            onSubmit={handleSubmit}
          />
        }
      >
        <div className="flex align-center justify-between py-10px">
          <h3 className="text-14 w-500">Allow donation</h3>
          <Switch checked={enabled} onChange={onToggle} />
        </div>
        <p className="mt-10px">
          When enabled, a donation option will appear during checkout, allowing
          users to contribute a chosen amount. A popup will display the donation
          sum before finalizing the purchase.
        </p>

        {enabled && (
          <div className="mt-16px radius-16 border-1px py-15px px-16px">
            <h4 className="text-12 w-600">Preselected amount</h4>

            <div className="mt-12px">
              {preselected.length > 0
                ? preselected.map((amount, index) => {
                    return (
                      <div
                        key={`${amount}${index}`}
                        className="py-15px px-12px flex align-center justify-between border-1px radius-12 mt-10px"
                      >
                        <p className="text-black">
                          <span className="inline mr-5px text-gray">$</span>
                          {amount.toFixed(2)}
                        </p>

                        <X
                          className="pointer"
                          size={20}
                          color="#000"
                          weight="light"
                          onClick={() => {
                            setPreselected(prev =>
                              prev.filter((_, i) => i !== index),
                            )
                          }}
                        />
                      </div>
                    )
                  })
                : null}
            </div>
            <Input
              type="number"
              placeholder="$"
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              className="mt-10px br-12"
            />
            <div className="mt-12">
              <div className="border-top mt-12px py-12px">
                <button
                  type="button"
                  onClick={handleAddValue}
                  className="text-prim border-none flex align-center justify-center gap-10px bg-transparent pointer"
                >
                  <Plus size={20} color="#2BA7CA" /> Add value
                </button>
              </div>
            </div>
          </div>
        )}
        <p className="mt-12px text-13 w-400">
          If no amount is selected, parents can still enter a custom donation
          amount.
        </p>
      </CustomModal>
    </>
  )
}

export default DonationModal
